import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Servicio global para gestionar el estado de loading/spinner.
 *
 * Mantiene un contador de peticiones activas para evitar múltiples spinners.
 * Aplica anti-flash: el overlay no se muestra si la carga termina antes de
 * SHOW_DELAY_MS, y una vez visible permanece al menos MIN_VISIBLE_MS para
 * evitar parpadeos en peticiones rápidas.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private static readonly SHOW_DELAY_MS = 200;
  private static readonly MIN_VISIBLE_MS = 250;

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  public readonly loading$ = this.loadingSubject.asObservable();

  private activeRequests = 0;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private shownAt: number | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Incrementa el contador de peticiones activas.
   * Si es la primera, programa la aparición del spinner tras SHOW_DELAY_MS.
   */
  show(): void {
    this.activeRequests++;
    if (this.activeRequests > 1) return;

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }

    if (this.loadingSubject.value || this.showTimer) return;

    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.shownAt = Date.now();
      this.loadingSubject.next(true);
    }, LoadingService.SHOW_DELAY_MS);
  }

  /**
   * Decrementa el contador. Si no hay más peticiones, oculta el spinner
   * respetando el tiempo mínimo de visibilidad.
   */
  hide(): void {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeRequests > 0) return;

    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
      return;
    }

    if (!this.loadingSubject.value) return;

    const elapsed = this.shownAt ? Date.now() - this.shownAt : LoadingService.MIN_VISIBLE_MS;
    const remaining = Math.max(0, LoadingService.MIN_VISIBLE_MS - elapsed);

    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.shownAt = null;
      if (this.activeRequests === 0) this.loadingSubject.next(false);
    }, remaining);
  }

  /**
   * Reinicia el contador y oculta el spinner.
   * Útil para manejar errores o limpieza.
   */
  reset(): void {
    this.activeRequests = 0;
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.shownAt = null;
    this.loadingSubject.next(false);
  }

  /**
   * Obtiene el estado actual de loading.
   */
  get isLoading(): boolean {
    return this.loadingSubject.value;
  }
}
